// ============================================================
// client.ts — 後端 API 請求封裝
// 統一管理所有 HTTP 請求，包含錯誤處理與 JWT Token 注入
// ============================================================

import axios, { AxiosInstance, AxiosResponse, AxiosError } from 'axios';
import {
    PostureSessionPayload,
    AIFeedbackResponse,
    StaffProgress,
    Staff,
} from 'types/posture';

// ── API 基礎設定 ──────────────────────────────────────────
const BASE_URL = process.env.REACT_APP_API_URL ?? 'http://localhost:3001';

// 建立 Axios 實例（統一設定）
const apiClient: AxiosInstance = axios.create({
    baseURL: BASE_URL,
    timeout: 30000,                       // 30 秒超時（AI 生成建議需要時間）
    headers: { 'Content-Type': 'application/json' },
});

// ── 請求攔截器：自動注入 JWT Token ────────────────────────
apiClient.interceptors.request.use(
    config => {
        const token = localStorage.getItem('auth_token');
        if (token) {
            config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
    },
    (error: AxiosError) => Promise.reject(error)
);

// ── 回應攔截器：統一錯誤處理 ─────────────────────────────
apiClient.interceptors.response.use(
    (response: AxiosResponse) => response,
    (error: AxiosError) => {
        if (error.response?.status === 401) {
            // Token 過期，重定向至登入頁
            localStorage.removeItem('auth_token');
            window.location.href = '/login';
        }
        console.error('[API Error]', error.response?.data ?? error.message);
        return Promise.reject(error);
    }
);

// ── API 函式封裝 ──────────────────────────────────────────

/**
 * 提交訓練 Session 數據至後端（觸發 AI 評分生成）
 */
export async function submitPostureSession(
    payload: PostureSessionPayload
): Promise<AIFeedbackResponse> {
    const res = await apiClient.post<AxiosResponse<AIFeedbackResponse>>('/api/posture/session', payload);
    return res.data.data;
}

/**
 * 獲取員工歷史進度統計
 */
export async function getStaffProgress(staffId: number): Promise<StaffProgress> {
    const res = await apiClient.get<AxiosResponse<StaffProgress>>(`/api/progress/${staffId}`);
    console.log("getStaffProgress", res);
    return res.data.data;
}

/**
 * 獲取全部員工列表（管理員用）
 */
export async function getAllStaff(): Promise<Staff[]> {
    const res = await apiClient.get<AxiosResponse<Staff[]>>('/api/progress/staff/all');
    return res.data.data;
}

/**
 * 生成 PDF 個人報告
 */
export async function generateReport(staffId: number): Promise<Blob> {
    const res = await apiClient.get<AxiosResponse<Blob>>(`/api/reports/${staffId}/pdf`, {
        responseType: 'blob',
    });
    return res.data.data;
}

/**
 * 員工登入
 */
export async function loginStaff(
    employeeId: string,
    password: string
): Promise<{ token: string; staff: Staff }> {
    const res = await apiClient.post('/api/posture/auth/login', { employeeId, password });
    return res.data;
}
